import React from 'react';
import { useQuery, gql } from '@apollo/client';
import StudentProspectsPage from '../../components/Succession/StudentProspectsPage';
import ApolloWrapper from '../StudentPlatform/ApolloWrapper';

const GET_STUDENT_PROSPECTS = gql`
  query GetStudentProspects(
    $school: String
    $degree_program: String
    $graduation_year: Int
    $employment_type: String
    $candidate_track: String
    $state: String
  ) {
    currentUser {
      id
      name
      email
    }
    studentProspects(
      school: $school
      degree_program: $degree_program
      graduation_year: $graduation_year
      employment_type: $employment_type
      candidate_track: $candidate_track
      state: $state
    ) {
      id
      candidate_track
      age
      phone
      city
      state
      school
      degree_program
      graduation_year
      employment_type
      experience_level
      desired_location
      candidate_interests
      family_in_business
      resume_url
      video_url
      avatar_url
      why_interested
      certifications
      linkedin_url
      profile_completion_percentage
      user {
        id
        name
        email
        allowed_name
        profile_picture
        user_name
      }
    }
    getPersonalConversations {
      id
      participant1 {
        id
      }
      participant2 {
        id
      }
    }
  }
`;

const StudentProspectsInner = () => {
  const [filters, setFilters] = React.useState({
    school: null,
    degree_program: null,
    graduation_year: null,
    employment_type: null,
    candidate_track: null,
    state: null,
  });

  const { loading, error, data, refetch } = useQuery(GET_STUDENT_PROSPECTS, {
    variables: filters,
  });

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    refetch(newFilters);
  };

  // Debug logging
  React.useEffect(() => {
    if (data) {
      console.log('StudentProspects data loaded:', {
        studentCount: data.studentProspects?.length || 0,
        students: data.studentProspects,
        currentUser: data.currentUser,
      });
    }
  }, [data]);

  React.useEffect(() => {
    if (error) {
      console.error('StudentProspects error:', error);
    }
  }, [error]);

  // Determine which students the advisor is already messaging with
  const messagingWithStudentIds = React.useMemo(() => {
    const conversations = data?.getPersonalConversations || [];
    const currentUserId = data?.currentUser?.id;
    const ids = new Set();
    
    if (currentUserId) {
      conversations.forEach(conv => {
        // Null check for participants
        if (conv?.participant1 && conv?.participant2) {
          const otherParticipantId = 
            conv.participant1.id === currentUserId 
              ? conv.participant2.id 
              : conv.participant1.id;
          ids.add(otherParticipantId);
        }
      });
    }
    
    return ids;
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-succession-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-succession-primary"></div>
          <p className="mt-4 text-succession-text-secondary">Loading student prospects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-succession-bg-primary flex items-center justify-center">
        <div className="text-center">
          <p className="text-succession-error mb-4">Failed to load student prospects</p>
          <p className="text-succession-text-secondary mt-2">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-succession-primary text-white rounded-md hover:bg-succession-primary-dark"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <StudentProspectsPage
      currentUser={data?.currentUser}
      students={data?.studentProspects || []}
      onFilterChange={handleFilterChange}
      filters={filters}
      messagingWithStudentIds={messagingWithStudentIds}
    />
  );
};

const StudentProspectsContainer = () => (
  <ApolloWrapper>
    <StudentProspectsInner />
  </ApolloWrapper>
);

export default StudentProspectsContainer;

